import { useState, useEffect } from 'react';
import { useReadContract, useWriteContract, useWalletClient } from 'wagmi';
import { type Auction, type AuctionParameters, type WinningBid } from '@chewy/typescript-config/marketplace-types';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { marketplaceV3Abi as englishAuctionsAbi } from '@/constants/abi/marketplaceV3Abi';
import { parseUnits, formatUnits, type Address } from 'viem';
import toast from '@/util/toast';
import { readContract } from 'wagmi/actions';
import { config } from '@/config/index';
import type { ChainType } from '@/constants/chain-specific';
type EnglishAuctionsParams = {
  chainId?: ChainType;
  contractAddress?: Address;
  collectionAddress?: Address;
};
export function useEnglishAuctions(params: EnglishAuctionsParams | ChainType = 109) {
  // Handle both the new object format and backward compatibility with just chainId
  const chainId = typeof params === 'object' ? params.chainId || 109 : params;
  const providedContractAddress = typeof params === 'object' ? params.contractAddress : undefined;
  const collectionAddress = typeof params === 'object' ? params.collectionAddress : undefined;
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const marketplaceContract = getMarketplaceContract(chainId);
  const marketplaceAddress = marketplaceContract?.address;
  // Use provided effectiveContractAddress if available, otherwise use the one from the marketplace contract
  const effectiveContractAddress = providedContractAddress || marketplaceAddress;
  const {
    data: walletClient
  } = useWalletClient();

  // Get total number of auctions
  const {
    data: totalAuctions,
    refetch: refetchTotalAuctions
  } = useReadContract({
    address: effectiveContractAddress as Address,
    abi: englishAuctionsAbi,
    functionName: 'totalAuctions',
    query: {
      enabled: !!effectiveContractAddress
    }
  });

  // Fetch all valid auctions
  const fetchAuctions = async () => {
    if (!effectiveContractAddress) return;
    try {
      setLoading(true);
      setError(null);
      const total = Number(totalAuctions || 0n);
      if (total > 0) {
        // Fetch auctions in batches (e.g., 10 at a time)
        const batchSize = 10;
        const batches = Math.ceil(total / batchSize);
        const allAuctions: Auction[] = [];
        for (let i = 0; i < batches; i++) {
          const startId = BigInt(i * batchSize);
          const endId = BigInt(Math.min((i + 1) * batchSize - 1, total - 1));
          console.log('Fetching auctions from', startId, 'to', endId);
          const batchAuctions = await readContract(config, {
            address: effectiveContractAddress as Address,
            abi: englishAuctionsAbi,
            functionName: 'getAllValidAuctions',
            args: [startId, endId],
            chainId
          });
          if (batchAuctions) {
            // Filter auctions by collection address if provided
            if (collectionAddress) {
              const filteredAuctions = (batchAuctions as Auction[]).filter(auction => {
                return auction.assetContract.toLowerCase() === collectionAddress.toLowerCase();
              });
              allAuctions.push(...filteredAuctions);
            } else {
              allAuctions.push(...(batchAuctions as Auction[]));
            }
          }
        }
        setAuctions(allAuctions);
      }
    } catch (err) {
      console.error('Error fetching auctions:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch auctions'));
    } finally {
      setLoading(false);
    }
  };

  // Create auction
  const {
    writeContract: createAuctionAsync,
    data: createAuctionData,
    error: createAuctionError,
    isPending: isCreatingAuction
  } = useWriteContract();

  // Cancel auction
  const {
    writeContract: cancelAuctionAsync,
    data: cancelAuctionData,
    error: cancelAuctionError,
    isPending: isCancellingAuction
  } = useWriteContract();

  // Bid in auction
  const {
    writeContract: bidInAuctionAsync,
    data: bidInAuctionData,
    error: bidInAuctionError,
    isPending: isBiddingInAuction
  } = useWriteContract();

  // Collect auction payout (for seller)
  const {
    writeContract: collectAuctionPayoutAsync,
    data: collectAuctionPayoutData,
    error: collectAuctionPayoutError,
    isPending: isCollectingPayout
  } = useWriteContract();

  // Collect auction tokens (for winner)
  const {
    writeContract: collectAuctionTokensAsync,
    data: collectAuctionTokensData,
    error: collectAuctionTokensError,
    isPending: isCollectingTokens
  } = useWriteContract();

  // Create a new auction
  const createAuction = async (assetContract: Address, tokenId: bigint, quantity: bigint, currency: Address, minimumBidAmount: string, buyoutBidAmount: string, timeBufferInSeconds: number, bidBufferBps: number, startTimestamp: number, endTimestamp: number) => {
    if (!effectiveContractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      // Convert price from string to bigint with appropriate decimals
      const minimumBidAmountBigInt = parseUnits(minimumBidAmount, 18);
      const buyoutBidAmountBigInt = parseUnits(buyoutBidAmount, 18);
      const params_0: AuctionParameters = {
        assetContract,
        tokenId,
        quantity,
        currency,
        minimumBidAmount: minimumBidAmountBigInt,
        buyoutBidAmount: buyoutBidAmountBigInt,
        timeBufferInSeconds: BigInt(timeBufferInSeconds),
        bidBufferBps: BigInt(bidBufferBps),
        startTimestamp: BigInt(startTimestamp),
        endTimestamp: BigInt(endTimestamp)
      };
      const tx = await createAuctionAsync({
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'createAuction',
        args: [params_0 as any]
      });
      toast.success('Auction created! Transaction sent.');
      return tx;
    } catch (err_0) {
      console.error('Error creating auction:', err_0);
      toast.error('Failed to create auction');
      throw err_0;
    }
  };

  // Cancel an existing auction
  const cancelAuction = async (auctionId: bigint) => {
    if (!effectiveContractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      const tx_0 = await cancelAuctionAsync({
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'cancelAuction',
        args: [auctionId]
      });
      toast.success('Auction cancelled! Transaction sent.');
      return tx_0;
    } catch (err_1) {
      console.error('Error cancelling auction:', err_1);
      toast.error('Failed to cancel auction');
      throw err_1;
    }
  };

  // Place a bid in an auction
  const bidInAuction = async (auctionId_0: bigint, bidAmount: string, isNativeCurrency: boolean = false) => {
    if (!effectiveContractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      // Convert bid amount from string to bigint with appropriate decimals
      const bidAmountBigInt = parseUnits(bidAmount, 18);
      const value = isNativeCurrency ? bidAmountBigInt : 0n;
      const tx_1 = await bidInAuctionAsync({
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'bidInAuction',
        args: [auctionId_0, bidAmountBigInt],
        value
      });
      toast.success('Bid placed! Transaction sent.');
      return tx_1;
    } catch (err_2) {
      console.error('Error placing bid:', err_2);
      toast.error('Failed to place bid');
      throw err_2;
    }
  };

  // For auction creator to collect the highest bid
  const collectAuctionPayout = async (auctionId_1: bigint) => {
    if (!effectiveContractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      const tx_2 = await collectAuctionPayoutAsync({
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'collectAuctionPayout',
        args: [auctionId_1]
      });
      toast.success('Payout collected! Transaction sent.');
      return tx_2;
    } catch (err_3) {
      console.error('Error collecting payout:', err_3);
      toast.error('Failed to collect payout');
      throw err_3;
    }
  };

  // For winning bidder to collect the auctioned NFT
  const collectAuctionTokens = async (auctionId_2: bigint) => {
    if (!effectiveContractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      const tx_3 = await collectAuctionTokensAsync({
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'collectAuctionTokens',
        args: [auctionId_2]
      });
      toast.success('NFT collected! Transaction sent.');
      return tx_3;
    } catch (err_4) {
      console.error('Error collecting NFT:', err_4);
      toast.error('Failed to collect NFT');
      throw err_4;
    }
  };

  // Get the winning bid for an auction
  const getWinningBid = async (auctionId_3: bigint): Promise<WinningBid | null> => {
    if (!effectiveContractAddress) return null;
    try {
      const data = await readContract(config, {
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'getWinningBid',
        args: [auctionId_3]
      });
      if (data) {
        const [bidder, currency_0, bidAmount_0] = data as [string, string, bigint];
        return {
          bidder: bidder as Address,
          currency: currency_0 as Address,
          bidAmount: bidAmount_0
        };
      }
      return null;
    } catch (err_5) {
      console.error('Error getting winning bid:', err_5);
      return null;
    }
  };

  // Check if an auction has expired
  const isAuctionExpired = async (auctionId_4: bigint): Promise<boolean> => {
    if (!effectiveContractAddress) return false;
    try {
      const data_0 = await readContract(config, {
        address: effectiveContractAddress as Address,
        abi: englishAuctionsAbi,
        functionName: 'isAuctionExpired',
        args: [auctionId_4]
      });
      return Boolean(data_0);
    } catch (err_6) {
      console.error('Error checking if auction expired:', err_6);
      return false;
    }
  };

  // Load auctions on mount and when chain or contract changes
  useEffect(() => {
    if (effectiveContractAddress) {
      fetchAuctions();
    }
  }, [effectiveContractAddress, totalAuctions, collectionAddress]);
  return {
    auctions,
    loading,
    error,
    refreshAuctions: fetchAuctions,
    createAuction,
    cancelAuction,
    bidInAuction,
    collectAuctionPayout,
    collectAuctionTokens,
    getWinningBid,
    isAuctionExpired,
    isCreatingAuction,
    isCancellingAuction,
    isBiddingInAuction,
    isCollectingPayout,
    isCollectingTokens
  };
}